// Linguify SaaS Translation Widget
// Embeddable translation widget for websites

interface TextNode {
  node: Node;
  originalText: string;
  key: string;
}

interface TranslationCache {
  [key: string]: string;
}

interface SiteConfig {
  targetLanguages: string[];
  apiKey: string;
  sourceLanguage?: string;
  brandColor?: string | null;
  widgetStyle?: {
    borderRadius?: string;
    fontFamily?: string;
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  };
  ignoreSelectors?: string[];
  hideOnPatterns?: string[];
  edgeHostnames?: string[];
  dynamicSelectors?: string[];
  edgeRouting?: boolean;
  languageDetection?: 'off' | 'browser' | 'geo';
}

interface LinguifyWindowConfig {
  apiKey?: string;
  apiBaseUrl?: string;
  dynamicSelectors?: string[];
  mountMode?: 'auto' | 'floating' | 'inline';
  mountSelector?: string;
  mountFallback?: 'floating' | 'hidden';
  edgeRouting?: boolean;
}

interface TranslationRequest {
  texts: string[];
  sourceLang: string;
  targetLang: string;
  siteApiKey: string;
}

interface TranslationResponse {
  translations: string[];
  cached: boolean;
}

const LANGUAGE_NAMES: { [key: string]: string } = {
  af: "Afrikaans", ar: "العربية", bg: "Български", bn: "বাংলা",
  ca: "Català", cs: "Čeština", cy: "Cymraeg", da: "Dansk",
  de: "Deutsch", el: "Ελληνικά", en: "English", es: "Español",
  et: "Eesti", eu: "Euskara", fa: "فارسی", fi: "Suomi",
  fr: "Français", ga: "Gaeilge", gl: "Galego", he: "עברית",
  hi: "हिन्दी", hr: "Hrvatski", hu: "Magyar", id: "Bahasa Indonesia",
  is: "Íslenska", it: "Italiano", ja: "日本語", ko: "한국어",
  lt: "Lietuvių", lv: "Latviešu", mk: "Македонски", ms: "Bahasa Melayu",
  mt: "Malti", nl: "Nederlands", no: "Norsk", pl: "Polski",
  pt: "Português", ro: "Română", ru: "Русский", sk: "Slovenčina",
  sl: "Slovenščina", sq: "Shqip", sr: "Српски", sv: "Svenska",
  sw: "Kiswahili", th: "ไทย", tr: "Türkçe", uk: "Українська",
  ur: "اردو", vi: "Tiếng Việt", zh: "中文",
};

const LANGUAGE_FLAGS: { [key: string]: string } = {
  af: "🇿🇦", ar: "🇸🇦", bg: "🇧🇬", bn: "🇧🇩",
  ca: "🇪🇸", cs: "🇨🇿", cy: "🏴󠁧󠁢󠁷󠁬󠁳󠁿", da: "🇩🇰",
  de: "🇩🇪", el: "🇬🇷", en: "🇬🇧", es: "🇪🇸",
  et: "🇪🇪", eu: "🇪🇸", fa: "🇮🇷", fi: "🇫🇮",
  fr: "🇫🇷", ga: "🇮🇪", gl: "🇪🇸", he: "🇮🇱",
  hi: "🇮🇳", hr: "🇭🇷", hu: "🇭🇺", id: "🇮🇩",
  is: "🇮🇸", it: "🇮🇹", ja: "🇯🇵", ko: "🇰🇷",
  lt: "🇱🇹", lv: "🇱🇻", mk: "🇲🇰", ms: "🇲🇾",
  mt: "🇲🇹", nl: "🇳🇱", no: "🇳🇴", pl: "🇵🇱",
  pt: "🇵🇹", ro: "🇷🇴", ru: "🇷🇺", sk: "🇸🇰",
  sl: "🇸🇮", sq: "🇦🇱", sr: "🇷🇸", sv: "🇸🇪",
  sw: "🇰🇪", th: "🇹🇭", tr: "🇹🇷", uk: "🇺🇦",
  ur: "🇵🇰", vi: "🇻🇳", zh: "🇨🇳",
};

class LinguifyWidget {
  private config: SiteConfig | null = null;
  private currentLanguage = "original";
  private textNodes: Map<string, TextNode> = new Map();
  private translationCache: Map<string, TranslationCache> = new Map();
  private containerElement: HTMLElement | null = null;
  private selectorElement: HTMLElement | null = null;
  private isExpanded = false;
  private inlineMode: boolean = false;
  private mutationObserver: MutationObserver | null = null;
  private mutationTimeout: number | null = null;
  private dynamicSelectors: string[] = [];
  private pendingMutationNodes: Set<Node> = new Set();
  private isLoading = false;
  private retryAttempts = 3;
  private retryDelay = 1000; // ms
  // Frozen at widget init. The URL / HTML lang on the first render is
  // the TRUE source of the text we captured in textNodes. After the
  // widget starts rewriting the DOM and the URL via pushState, the URL
  // prefix drifts to the target language — reading it as "source" at
  // that point would ask the API to translate English text as if it
  // were German, which blanks the page. Freeze once, use forever.
  private initialServerLang: string = "en";
  // True when the customer site is served by the Linguify edge (URLs
  // carry /<lang>/ prefixes). Language switches navigate to the target
  // URL instead of doing a DOM-swap. Determined once in initialize().
  private edgeRoutingActive: boolean = false;

  constructor() {
    const saved = this.loadLanguagePreference();
    if (saved) this.currentLanguage = saved;
  }

  private displayFor(lang: string): { flag: string; name: string } {
    if (lang === "original") {
      const src = this.config?.sourceLanguage;
      const flag = (src && LANGUAGE_FLAGS[src]) || "🌐";
      const name = (src && LANGUAGE_NAMES[src]) || "Original";
      return { flag, name };
    }
    return {
      flag: LANGUAGE_FLAGS[lang] || "🌐",
      name: LANGUAGE_NAMES[lang] || lang.toUpperCase(),
    };
  }

  async initialize(): Promise<void> {
    try {
      // Get config from window
      const windowConfig = (window as any).LINGUIFY_CONFIG;
      if (!windowConfig || !windowConfig.apiKey) {
        console.error("Linguify: No LINGUIFY_CONFIG found on window");
        return;
      }

      // Fetch site config from API
      this.config = await this.fetchSiteConfig(windowConfig.apiKey);
      if (!this.config) {
        console.error("Linguify: Failed to fetch site configuration");
        return;
      }

      // Hide on URL patterns configured per-site (e.g. /admin/*).
      const path = window.location.pathname;
      const patterns = this.config.hideOnPatterns || [];
      const match = patterns.some((p) => {
        const re = new RegExp('^' + p.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$');
        return re.test(path);
      });
      if (match) return;

      // Determine edge routing mode once, before any translate logic runs.
      // Precedence: explicit window override > backend flag > URL heuristic.
      // Conservative: if none of the signals fire, edgeRoutingActive stays false.
      this.edgeRoutingActive = this.detectEdgeRouting(windowConfig);

      // When in edge mode, set currentLanguage from the URL prefix so the
      // switcher pill shows the correct language on first render.
      if (this.edgeRoutingActive) {
        const sourceLang = this.config.sourceLanguage || "en";
        const supportedLangs = new Set([sourceLang, ...this.config.targetLanguages]);
        const pm = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
        if (pm && supportedLangs.has(pm[1])) {
          this.currentLanguage = pm[1];
        } else {
          this.currentLanguage = sourceLang;
        }
      }

      // Merge dynamic selectors from window config (local override) and
      // backend config. If neither specifies selectors, default is empty
      // which means "translate ALL new text nodes" (opt-out model).
      this.dynamicSelectors = [
        ...(windowConfig.dynamicSelectors || []),
        ...(this.config.dynamicSelectors || []),
      ];

      // Freeze the server-rendered language BEFORE anything else that
      // could trigger a translate call. Order matters: if we set up
      // the MutationObserver first and then compute server lang, a
      // mutation firing in the gap reads the default "en" and asks
      // the API to translate German-as-English — which returns empty
      // strings and blanks the DOM.
      this.initialServerLang = this.computeServerLang();

      // Create language selector UI
      this.createSelector();

      // Collect initial text nodes
      this.collectTextNodes();

      // Setup mutation observer for dynamic content — has to come AFTER
      // initialServerLang is frozen (see comment above).
      this.setupMutationObserver();

      // Hydrate the translation cache from localStorage before touching
      // the network. On the dashboard (SPA) this makes the second visit
      // in a session render in the chosen language with zero API calls.
      this.loadCacheFromStorage();

      // Restore the user's saved language (or auto-detect on first visit).
      await this.restoreOrAutoDetect();

      // Eager prefetch all target languages in parallel to warm the cache
      this.prefetchAllLanguages();
    } catch (error) {
      console.error("Linguify initialization error:", error);
    }
  }

  // localStorage persistence key for translationCache. Keyed by the
  // site's apiKey so multiple sites embedded on the same origin don't
  // collide, and so cache invalidation = bump the key version.
  //
  // v2: wipes entries saved by v1, which under buggy earlier widget
  // versions stored empty-string values for many source strings (we
  // sent German-as-English to the API and got back ""s, then persisted).
  // Those empties wouldn't hurt applyTranslations (it has a falsy
  // guard), but other paths — loadLanguagePreference, prefetch — could
  // still surface them. Fresh key = fresh state.
  private cacheStorageKey(): string {
    const apiKey = this.config?.apiKey || "unknown";
    return `linguify-tcache-v3:${apiKey}`;
  }

  private loadCacheFromStorage(): void {
    try {
      // Proactively clear the old v1 key so it doesn't accumulate forever
      // in localStorage alongside v2. Silent; works when storage is
      // disabled and just skips.
      const apiKey = this.config?.apiKey || "unknown";
      try { localStorage.removeItem(`linguify-tcache-v1:${apiKey}`); } catch { /* ok */ }
      try { localStorage.removeItem(`linguify-tcache-v2:${apiKey}`); } catch { /* ok */ }
      const raw = localStorage.getItem(this.cacheStorageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, TranslationCache>;
      for (const lang of Object.keys(parsed)) {
        // Filter out any empty-string entries defensively — if a future
        // bug ever poisons the cache again, we don't carry the damage
        // across reloads.
        const clean: TranslationCache = {};
        const src = parsed[lang] || {};
        for (const k of Object.keys(src)) {
          if (src[k]) clean[k] = src[k];
        }
        this.translationCache.set(lang, clean);
      }
    } catch {
      // storage disabled or corrupt; safe to ignore — worst case we
      // re-fetch translations once.
    }
  }

  private persistCacheToStorage(): void {
    try {
      const obj: Record<string, TranslationCache> = {};
      for (const [lang, cache] of this.translationCache.entries()) {
        obj[lang] = cache;
      }
      localStorage.setItem(this.cacheStorageKey(), JSON.stringify(obj));
    } catch {
      // QuotaExceeded or storage disabled — silently give up; the cache
      // stays in-memory for this session.
    }
  }

  private prefetchAllLanguages(): void {
    if (!this.config) return;
    // Edge-hosted sites now also DOM-swap language, so the in-memory
    // cache helps there too — warms so the click feels instant.
    const serverLang = this.initialServerLang;
    const sourceLang = this.config.sourceLanguage || "en";
    const langs = this.config.targetLanguages.filter(
      (l) => l !== this.currentLanguage && l !== serverLang && l !== sourceLang,
    );
    // Fire-and-forget in parallel; errors are logged inside translateAllText.
    langs.forEach((lang) => {
      this.prefetchLanguage(lang);
    });
  }

  private async prefetchLanguage(targetLang: string): Promise<void> {
    if (!this.config) return;
    try {
      if (!this.translationCache.has(targetLang)) {
        this.translationCache.set(targetLang, {});
      }
      const cache = this.translationCache.get(targetLang)!;
      // Same reasoning as translateAllText: the text in the DOM is
      // whatever the server rendered, not necessarily the site's
      // configured source language. Keep them aligned so the API can
      // translate correctly.
      const sourceLang = this.initialServerLang;
      if (sourceLang === targetLang) return;
      // Skip strings we already have cached — lets prefetch run cheaply
      // on every page navigation so new strings get translated while
      // already-known ones avoid a round-trip.
      const unique = Array.from(
        new Set(Array.from(this.textNodes.values()).map((t) => t.originalText)),
      ).filter((src) => cache[src] === undefined);
      if (!unique.length) return;
      const batchSize = 50;
      const batches: string[][] = [];
      for (let i = 0; i < unique.length; i += batchSize) {
        batches.push(unique.slice(i, i + batchSize));
      }
      await Promise.all(
        batches.map(async (batch) => {
          const translations = await this.fetchTranslations(batch, sourceLang, targetLang);
          if (!translations) return;
          batch.forEach((src, idx) => {
            const t = translations[idx];
            // Never store empty/falsy translations — they'd poison the
            // cache and applyTranslations' falsy guard would skip them
            // harmlessly but prefetch-from-cache paths would still see
            // them as "present". Skip; next visit retries.
            if (t) cache[src] = t;
          });
        }),
      );
      this.persistCacheToStorage();
    } catch (error) {
      console.warn("Linguify: prefetch failed for", targetLang, error);
    }
  }

  private async fetchSiteConfig(apiKey: string): Promise<SiteConfig | null> {
    try {
      const response = await this.fetchWithRetry(
        `${this.getApiBaseUrl()}/api/widget/config`,
        {
          method: "GET",
          headers: {
            "x-api-key": apiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return {
        targetLanguages: data.targetLanguages || [],
        apiKey: apiKey,
        sourceLanguage: data.sourceLanguage,
        brandColor: data.brandColor || null,
        widgetStyle: data.widgetStyle || {},
        ignoreSelectors: data.ignoreSelectors || [],
        hideOnPatterns: data.hideOnPatterns || [],
        edgeHostnames: data.edgeHostnames || [],
        dynamicSelectors: data.dynamicSelectors || [],
        edgeRouting: typeof data.edgeRouting === 'boolean' ? data.edgeRouting : undefined,
        languageDetection: data.languageDetection || 'browser',
      };
    } catch (error) {
      console.error("Linguify: Error fetching site config:", error);
      return null;
    }
  }

  private resolveMountTarget(): { target: HTMLElement | null; inline: boolean } {
    const windowConfig = (window as any).LINGUIFY_CONFIG as LinguifyWindowConfig | undefined;
    const mountMode = windowConfig?.mountMode ?? 'auto';
    const mountFallback = windowConfig?.mountFallback ?? 'floating';

    if (mountMode === 'floating') {
      return { target: null, inline: false };
    }

    // Level 1: explicit data attribute
    const explicit = document.querySelector('[data-linguify-switcher]') as HTMLElement | null;
    if (explicit) return { target: explicit, inline: true };

    // Level 2: config selector
    if (windowConfig?.mountSelector) {
      try {
        const sel = document.querySelector(windowConfig.mountSelector) as HTMLElement | null;
        if (sel) return { target: sel, inline: true };
      } catch {
        // invalid selector
      }
    }

    if (mountMode === 'inline') {
      console.warn('[Linguify] mountMode=inline but no mount target found — widget not rendered');
      return { target: null, inline: false };
    }

    // Level 3: auto-detect navbar (mountMode === 'auto')
    const navCandidates: string[] = [
      'header nav',
      'nav[role=navigation]',
      'header[role=banner] nav',
      'nav.main-nav',
      'nav.primary-nav',
      'nav.navbar',
      'nav.site-nav',
      '.header nav',
      '.site-header nav',
    ];
    for (const sel of navCandidates) {
      try {
        const el = document.querySelector(sel) as HTMLElement | null;
        if (el) return { target: el, inline: true };
      } catch {
        // invalid selector, skip
      }
    }
    // Last resort: single nav in the entire document
    const allNavs = document.querySelectorAll('nav');
    if (allNavs.length === 1) {
      return { target: allNavs[0] as HTMLElement, inline: true };
    }

    // Level 4: fallback
    if (mountFallback === 'hidden') return { target: null, inline: false };
    return { target: null, inline: false };
  }

  private createSelector(): void {
    if (!this.config) return;

    // Container
    this.containerElement = document.createElement("div");
    this.containerElement.id = "linguify-widget-container";
    this.containerElement.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 999999;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    `;

    // Selector pill
    this.selectorElement = document.createElement("div");
    this.selectorElement.id = "linguify-selector";
    this.selectorElement.style.cssText = `
      background: white;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      padding: 6px 10px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transition: all 0.2s ease;
      user-select: none;
    `;

    const { flag: currentFlag, name: currentName } = this.displayFor(this.currentLanguage);
    const displayCode = this.currentLanguage === "original"
      ? (this.config?.sourceLanguage || "original").toUpperCase()
      : this.currentLanguage.toUpperCase();
    if (this.config?.brandColor) {
      this.selectorElement.style.background = this.config.brandColor;
      this.selectorElement.style.borderColor = this.config.brandColor;
    }
    const style = this.config?.widgetStyle || {};
    if (style.borderRadius) this.selectorElement.style.borderRadius = style.borderRadius;
    if (style.fontFamily) this.selectorElement.style.fontFamily = style.fontFamily;
    const pos = style.position;
    if (this.containerElement) {
      const c = this.containerElement.style;
      c.bottom = pos?.startsWith('top') ? '' : '20px';
      c.top = pos?.startsWith('top') ? '20px' : '';
      c.right = pos?.endsWith('right') || !pos ? '20px' : '';
      c.left = pos?.endsWith('left') ? '20px' : '';
    }

    const displayText = document.createElement("span");
    displayText.style.cssText = `
      font-size: 13px;
      font-weight: 500;
      color: #333;
      white-space: nowrap;
    `;
    displayText.textContent = `${currentFlag} ${displayCode}`;
    this.selectorElement.title = currentName;

    const chevron = document.createElement("span");
    chevron.style.cssText = `
      font-size: 12px;
      color: #999;
      transition: transform 0.2s ease;
    `;
    chevron.textContent = "▾";

    this.selectorElement.appendChild(displayText);
    this.selectorElement.appendChild(chevron);

    this.selectorElement.addEventListener("click", () =>
      this.toggleSelector()
    );

    // Alt+L keyboard shortcut toggles the language menu.
    document.addEventListener("keydown", (e) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.key.toLowerCase() === "l") {
        e.preventDefault();
        this.toggleSelector();
      }
    });
    this.selectorElement.addEventListener("mouseenter", () => {
      if (!this.isExpanded) {
        this.selectorElement!.style.boxShadow =
          "0 4px 12px rgba(0, 0, 0, 0.15)";
      }
    });
    this.selectorElement.addEventListener("mouseleave", () => {
      if (!this.isExpanded) {
        this.selectorElement!.style.boxShadow =
          "0 2px 8px rgba(0, 0, 0, 0.1)";
      }
    });

    // Loading indicator
    const loadingIndicator = document.createElement("div");
    loadingIndicator.id = "linguify-loading";
    loadingIndicator.style.cssText = `
      display: none;
      width: 16px;
      height: 16px;
      border: 2px solid #f0f0f0;
      border-top: 2px solid #3498db;
      border-radius: 50%;
      animation: linguify-spin 0.6s linear infinite;
      position: fixed;
      bottom: 28px;
      right: 28px;
      z-index: 999998;
    `;

    // Add keyframe animation
    if (!document.getElementById("linguify-styles")) {
      const style = document.createElement("style");
      style.id = "linguify-styles";
      style.textContent = `
        @keyframes linguify-spin {
          to { transform: rotate(360deg); }
        }
        #linguify-selector:hover {
          background: #f9f9f9;
        }
        [hidden] { display: none !important; }
      `;
      document.head.appendChild(style);
    }

    const { target: mountTarget, inline } = this.resolveMountTarget();

    if (inline && mountTarget) {
      this.inlineMode = true;
      // Defensive container styles: don't fight the customer's flex/grid nav
      this.containerElement.style.cssText = `
        position: relative;
        display: inline-flex;
        flex-shrink: 0;
        align-items: center;
        vertical-align: middle;
        font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        margin-left: 16px;
      `;
      this.containerElement.appendChild(this.selectorElement);
      document.body.appendChild(loadingIndicator);

      // If parent nav has overflow:hidden the dropdown will be clipped.
      // Detect it now (before appendChild so we read the nav's own style)
      // and record whether we need portal rendering for the menu.
      const navOverflow = getComputedStyle(mountTarget).overflow;
      if (navOverflow !== 'visible') {
        this.containerElement.dataset.linguifyPortal = 'true';
      }

      mountTarget.appendChild(this.containerElement);
      console.info('[Linguify] mounted inline at:', mountTarget);
    } else {
      this.inlineMode = false;
      this.containerElement.appendChild(this.selectorElement);
      this.containerElement.appendChild(loadingIndicator);
      document.body.appendChild(this.containerElement);
      console.info('[Linguify] mounted floating (no nav target found)');
    }
  }

  private toggleSelector(): void {
    if (!this.config) return;

    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.showLanguageMenu();
    } else {
      this.hideLanguageMenu();
    }
  }

  private showLanguageMenu(): void {
    if (!this.config || !this.containerElement) return;

    // Remove existing menu
    const existing = document.getElementById("linguify-menu");
    if (existing) existing.remove();

    const menu = document.createElement("div");
    menu.id = "linguify-menu";

    // Use the viewport-aware fixed-position path for every inline mount, not
    // only navs with overflow:hidden. An inline switcher living in a customer
    // nav can be clipped two ways: by an ancestor's overflow, OR simply by the
    // bottom edge of the screen when it sits low in a mobile menu. The old
    // absolute `top: calc(100% + 4px)` branch had no viewport awareness and
    // ran the dropdown off-screen on mobile. Floating (non-inline) mode keeps
    // its own container-relative positioning.
    const usePortal = this.inlineMode || this.containerElement.dataset.linguifyPortal === 'true';

    if (usePortal) {
      // Mount to body and position via getBoundingClientRect so the dropdown
      // escapes any clip region. Flip up when there isn't room below (e.g. the
      // switcher sits near the bottom of a mobile menu — otherwise the list is
      // cut off by the viewport edge), clamp horizontally so it never spills
      // off-screen, and cap the height to the available space.
      const rect = this.containerElement.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const MENU_W = 170;
      const spaceBelow = vh - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
      const maxH = Math.max(140, Math.min(320, (openUp ? spaceAbove : spaceBelow) - 12));
      const left = Math.max(8, Math.min(rect.left, vw - MENU_W - 8));
      const vertical = openUp
        ? `bottom: ${Math.round(vh - rect.top + 4)}px; top: auto;`
        : `top: ${Math.round(rect.bottom + 4)}px; bottom: auto;`;
      menu.style.cssText = `
        position: fixed;
        ${vertical}
        left: ${Math.round(left)}px;
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        padding: 8px 0;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        min-width: 110px;
        max-width: ${vw - 16}px;
        z-index: 999999;
        max-height: ${Math.round(maxH)}px;
        overflow-y: auto;
        transform: translateZ(0);
        -webkit-overflow-scrolling: touch;
      `;
      document.body.appendChild(menu);
    } else {
      // Normal absolute positioning relative to container
      const pos = this.config?.widgetStyle?.position || 'bottom-right';
      const verticalCss = this.inlineMode || pos.startsWith('top')
        ? 'top: calc(100% + 4px); bottom: auto;'
        : 'bottom: calc(100% + 4px); top: auto;';
      const horizontalCss = pos.endsWith('left') && !this.inlineMode ? 'left: 0;' : 'right: 0;';
      menu.style.cssText = `
        position: absolute;
        ${verticalCss}
        ${horizontalCss}
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        padding: 8px 0;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        min-width: 110px;
        z-index: 999998;
        max-height: 300px;
        overflow-y: auto;
        transform: translateZ(0);
      `;
      this.containerElement.appendChild(menu);
    }

    // "Original" option always first — restores source text regardless of source lang
    this.addLanguageOption(menu, "original");

    // Add target languages
    this.config.targetLanguages.forEach((lang) => {
      this.addLanguageOption(menu, lang);
    });

    // Close menu on outside click
    const closeHandler = (e: MouseEvent) => {
      if (
        !this.containerElement?.contains(e.target as Node) &&
        !menu.contains(e.target as Node)
      ) {
        this.hideLanguageMenu();
        document.removeEventListener("click", closeHandler);
      }
    };

    setTimeout(() => {
      document.addEventListener("click", closeHandler);
    }, 0);
  }

  private addLanguageOption(
    menu: HTMLElement,
    langCode: string
  ): void {
    const option = document.createElement("div");
    option.style.cssText = `
      padding: 8px 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: background 0.15s ease;
      user-select: none;
    `;

    const { flag: rawFlag, name: rawName } = this.displayFor(langCode);
    const isOriginal = langCode === "original";
    const flag = rawFlag;
    const displayCode = isOriginal
      ? (this.config?.sourceLanguage || "original").toUpperCase()
      : langCode.toUpperCase();
    option.title = isOriginal ? `Original (${rawName})` : rawName;

    const flagSpan = document.createElement("span");
    flagSpan.style.fontSize = "16px";
    flagSpan.textContent = flag;

    const nameSpan = document.createElement("span");
    nameSpan.style.cssText = `
      flex: 1;
      font-size: 13px;
      color: #333;
    `;
    nameSpan.textContent = displayCode;

    const checkmark = document.createElement("span");
    checkmark.style.cssText = `
      font-size: 14px;
      color: #3498db;
      display: ${langCode === this.currentLanguage ? "block" : "none"};
    `;
    checkmark.textContent = "✓";

    option.appendChild(flagSpan);
    option.appendChild(nameSpan);
    option.appendChild(checkmark);

    option.addEventListener("mouseenter", () => {
      option.style.background = "#f5f5f5";
    });

    option.addEventListener("mouseleave", () => {
      option.style.background = "transparent";
    });

    option.addEventListener("click", (e) => {
      e.stopPropagation();
      this.selectLanguage(langCode);
      this.hideLanguageMenu();
    });

    menu.appendChild(option);
  }

  private hideLanguageMenu(): void {
    const menu = document.getElementById("linguify-menu");
    if (menu) {
      menu.remove();
    }
    this.isExpanded = false;
  }

  // Returns true when language switches should navigate to /<lang>/ URLs
  // rather than DOM-swapping in place.
  private detectEdgeRouting(windowConfig: LinguifyWindowConfig): boolean {
    // Explicit window opt-out always wins.
    if (windowConfig.edgeRouting === false) return false;
    // Backend flag (derived from verified custom_domains) only applies when
    // the current host is actually served by the edge — otherwise the flag
    // is true for the whole site regardless of which embed we're running on.
    if (this.config?.edgeRouting === true) return this.isEdgeHost();
    if (this.config?.edgeRouting === false) return false;
    // URL heuristic: /<lang>/ prefix with a known language → edge mode.
    if (!this.config) return false;
    const sourceLang = this.config.sourceLanguage || "en";
    const supportedLangs = new Set([sourceLang, ...this.config.targetLanguages]);
    const m = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    return !!(m && supportedLangs.has(m[1]));
  }

  private urlForLang(targetLang: string): string {
    const sourceLang = this.config?.sourceLanguage || "en";
    const supportedLangs = new Set([sourceLang, ...(this.config?.targetLanguages || [])]);
    const path = window.location.pathname;
    const m = path.match(/^\/([a-z]{2})(\/|$)/);
    let bare = path;
    if (m && supportedLangs.has(m[1])) {
      bare = path.slice(3) || "/";
    }
    if (targetLang === sourceLang || targetLang === "original") {
      return bare + window.location.search + window.location.hash;
    }
    const newPath = bare === "/" ? "/" + targetLang + "/" : "/" + targetLang + bare;
    return newPath + window.location.search + window.location.hash;
  }

  // Hosts where the Linguify edge renderer is the origin — if we're on one
  // of these we can switch language by rewriting the URL path to /{lang}/...
  // and the edge will serve the translated HTML from KV cache. On any other
  // host we stay in pure JS DOM-replace mode (URL untouched).
  // One-shot: read the URL's /{lang}/ prefix at the time the widget
  // starts. Called exactly once in init(); callers after that should
  // use `this.initialServerLang` so post-pushState URL changes don't
  // corrupt the translate pipeline's source language.
  private computeServerLang(): string {
    const configured = this.config?.sourceLanguage || "en";
    if (!this.isEdgeHost() || !this.config) return configured;
    const segments = window.location.pathname.split("/").filter(Boolean);
    const first = segments[0];
    const known = new Set<string>([configured, ...this.config.targetLanguages]);
    if (first && known.has(first)) return first;
    return configured;
  }

  private isEdgeHost(): boolean {
    const hosts = this.config?.edgeHostnames || [];
    if (!hosts.length) return false;
    const current = window.location.hostname.toLowerCase();
    return hosts.some((h) => h.toLowerCase() === current);
  }

  private updateUrlForLang(langCode: string): void {
    if (!this.isEdgeHost() || !this.config) return;
    const source = this.config.sourceLanguage || "en";
    const targets = this.config.targetLanguages || [];
    const known = new Set<string>([source, ...targets]);
    const url = new URL(window.location.href);
    const segments = url.pathname.split("/").filter(Boolean);
    // Strip an existing lang prefix if present.
    if (segments.length && known.has(segments[0])) {
      segments.shift();
    }
    // Prepend the new lang unless we're going to source / original.
    if (langCode !== "original" && langCode !== source) {
      segments.unshift(langCode);
    }
    url.pathname = "/" + segments.join("/");
    // Avoid double slash on root path.
    if (url.pathname === "//") url.pathname = "/";
    // Drop ?linguify_lang= deep-link once we've encoded the choice in the path.
    url.searchParams.delete("linguify_lang");
    if (url.toString() !== window.location.href) {
      history.pushState(null, "", url.toString());
    }
  }

  private async selectLanguage(langCode: string): Promise<void> {
    if (langCode === this.currentLanguage) return;

    // Edge routing: navigate to the target URL so the edge serves
    // server-rendered translated HTML. No DOM-swap; URL stays canonical.
    if (this.edgeRoutingActive) {
      // Persist the user's explicit choice BEFORE navigation. Without this,
      // restoreOrAutoDetect on the landing page sees no saved preference and
      // falls back to navigator.language — which sends a Spanish browser
      // back to /es/ the moment they clicked EN. Trap.
      this.saveLanguagePreference(langCode);
      const targetUrl = this.urlForLang(langCode);
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      if (targetUrl !== currentUrl) {
        window.location.href = targetUrl;
        return;
      }
      // Same URL (e.g. already on /es/ clicking ES) — no-op.
      return;
    }

    this.currentLanguage = langCode;
    this.saveLanguagePreference(langCode);

    // DOM-swap for everyone, edge-hosted or not. A full navigation to
    // /{lang}/ was correct for SEO bots but jarring for visitors —
    // flash of white, scroll position lost, and the URL change reads
    // as "this isn't the page I was on". /es/, /de/, /fr/, /it/
    // subdirectories still exist as canonical SEO entry points; they
    // just stop being the widget's way of switching language mid-visit.
    this.updateUrlForLang(langCode);
    // On edge-hosted pages the textNodes map holds whatever language
    // the server rendered (Spanish on /es/, German on /de/ ...). That's
    // the real source for the next translation; don't "restore" back to
    // something the server didn't send.
    if (!this.isEdgeHost()) {
      this.restoreOriginalText();
    }
    // SPA apps (React/Vue) render most of their UI after the widget's
    // initial collectTextNodes() run, so the tracked set misses anything
    // rendered later. Augment with nodes present now, keeping the
    // originalText of already-tracked nodes intact so we don't capture
    // a prior translation as "source".
    this.augmentTextNodes();
    this.updateSelectorDisplay();
    this.syncPushState(langCode);

    if (langCode === "original") {
      // "Original" on edge-host means "render what the source URL would
      // have served". The DOM currently holds a pre-translated view, so
      // the only way back to the source is a soft re-render from the
      // server. Push the URL without the /lang/ prefix and reload so
      // the edge hands us English (cache hit, sub-50ms).
      if (this.isEdgeHost()) window.location.reload();
      return;
    }

    if (this.translationCache.has(langCode)) {
      this.applyTranslations(langCode);
      return;
    }

    await this.translateAllText(langCode);
  }

  // Reflect the chosen language in the URL path via pushState so the
  // address bar + bookmarks + share links line up with the visible copy.
  // No navigation triggered — we only rewrite the bar.
  private syncPushState(langCode: string): void {
    if (!this.isEdgeHost() || !this.config) return;
    const source = this.config.sourceLanguage || "en";
    const targets = this.config.targetLanguages || [];
    const known = new Set<string>([source, ...targets]);
    const url = new URL(window.location.href);
    const segments = url.pathname.split("/").filter(Boolean);
    if (segments.length && known.has(segments[0])) segments.shift();
    if (langCode !== "original" && langCode !== source) {
      segments.unshift(langCode);
    }
    url.pathname = "/" + segments.join("/");
    if (url.pathname === "//") url.pathname = "/";
    url.searchParams.delete("linguify_lang");
    try {
      window.history.pushState({}, "", url.toString());
    } catch { /* some browsers disallow pushState on mixed schemes */ }
  }

  // Walk the current DOM and add untracked text nodes to the map without
  // disturbing existing entries. Used right before every language switch
  // so SPA-rendered content gets picked up without waiting for a route
  // change to trigger handleFullRecollect.
  private augmentTextNodes(): void {
    const tracked = new Set<Node>();
    this.textNodes.forEach((tn) => tracked.add(tn.node));
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node: Node) => {
          if (this.containerElement?.contains(node)) return NodeFilter.FILTER_REJECT;
          if (this.shouldIgnoreNode(node)) return NodeFilter.FILTER_REJECT;
          const parentElement = node.parentElement;
          if (!parentElement ||
              /^(script|style|svg|textarea|input|code|pre)$/i.test(parentElement.tagName)) {
            return NodeFilter.FILTER_REJECT;
          }
          const text = node.textContent || "";
          if (!text.trim() || text.trim().length < 2) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      },
    );
    let node: Node | null;
    let nextIndex = this.textNodes.size;
    while ((node = walker.nextNode())) {
      if (tracked.has(node)) continue;
      const raw = node.textContent || "";
      const key = `node_${nextIndex++}`;
      this.textNodes.set(key, { node, originalText: raw, key });
    }
  }

  private updateSelectorDisplay(): void {
    const displayText = this.selectorElement?.querySelector("span");
    if (displayText) {
      const { flag, name } = this.displayFor(this.currentLanguage);
      const code = this.currentLanguage === "original"
        ? (this.config?.sourceLanguage || "original").toUpperCase()
        : this.currentLanguage.toUpperCase();
      displayText.textContent = `${flag} ${code}`;
      if (this.selectorElement) this.selectorElement.title = name;
    }
  }

  private collectTextNodes(): void {
    this.textNodes.clear();
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node: Node) => {
          const selectors = this.config?.ignoreSelectors || [];
          // Skip if an ancestor is marked do-not-translate via any of
          // our three opt-out mechanisms: data-linguify-ignore,
          // data-notranslate, or the Google-Translate-native
          // class="notranslate" (which the edge also masks).
          let parent = node.parentElement;
          while (parent) {
            if (parent.hasAttribute("data-linguify-ignore")) {
              return NodeFilter.FILTER_REJECT;
            }
            if (parent.hasAttribute("data-notranslate")) {
              return NodeFilter.FILTER_REJECT;
            }
            if (parent.classList && parent.classList.contains("notranslate")) {
              return NodeFilter.FILTER_REJECT;
            }
            if (selectors.length) {
              for (const sel of selectors) {
                try {
                  if (parent.matches(sel)) return NodeFilter.FILTER_REJECT;
                } catch {
                  // invalid selector, skip silently
                }
              }
            }
            parent = parent.parentElement;
          }

          // Skip scripts, styles, svg, textarea, input
          const parentElement = node.parentElement;
          if (
            !parentElement ||
            /^(script|style|svg|textarea|input|code|pre)$/i.test(
              parentElement.tagName
            )
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          // Skip if text is only whitespace or too short
          const text = node.textContent || "";
          if (!text.trim() || text.trim().length < 2) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        },
      }
    );

    let currentNode: Node | null;
    let index = 0;

    while ((currentNode = walker.nextNode())) {
      const raw = currentNode.textContent || "";
      if (raw.trim().length >= 2) {
        // Per-node entry (multiple DOM nodes may share the same text); the
        // translationCache is keyed by originalText so identical strings
        // share a single backend lookup and survive DOM mutations.
        const key = `node_${index}`;
        this.textNodes.set(key, {
          node: currentNode,
          originalText: raw,
          key,
        });
        index++;
      }
    }
  }

  private async translateAllText(targetLang: string): Promise<void> {
    if (this.isLoading) return;
    // Use whatever language the page was actually rendered in as the
    // source — not the site's configured sourceLanguage. On edge-
    // hosted sites the HTML we see may already be /de/ or /es/; sending
    // that text to the API as if it were English returned empty/
    // garbled strings and blanked the DOM. serverRenderedLang tracks
    // the real lang of what collectTextNodes captured.
    const serverLang = this.initialServerLang;
    if (targetLang === serverLang || targetLang === "original") {
      // Nothing to do; the DOM already matches the target.
      return;
    }
    this.setLoading(true);

    try {
      if (!this.translationCache.has(targetLang)) {
        this.translationCache.set(targetLang, {});
      }
      const cache = this.translationCache.get(targetLang)!;
      const sourceLang = serverLang;

      // Build the set of unique source strings that aren't yet translated.
      const missing = new Set<string>();
      this.textNodes.forEach((tn) => {
        if (cache[tn.originalText] === undefined) missing.add(tn.originalText);
      });

      const textArray = Array.from(missing);
      const batchSize = 50;
      const batches: string[][] = [];
      for (let i = 0; i < textArray.length; i += batchSize) {
        batches.push(textArray.slice(i, i + batchSize));
      }

      // Parallel fan-out instead of sequential await — with 300 strings
      // across 6 batches, sequential is ~6× slower. The backend queues
      // provider calls internally, so fanning out from the client side
      // is what gets the round-trip down from ~4s to ~700ms. Apply each
      // batch's results as it returns so the DOM updates progressively
      // instead of waiting for the last batch to finish.
      await Promise.all(
        batches.map(async (batch) => {
          const translations = await this.fetchTranslations(batch, sourceLang, targetLang);
          if (translations) {
            batch.forEach((src, idx) => {
              const t = translations[idx];
              if (t) cache[src] = t;
            });
            this.applyTranslations(targetLang);
          }
        }),
      );
      // Persist the freshly-translated strings so the next visit hits
      // localStorage instead of the backend.
      this.persistCacheToStorage();
    } catch (error) {
      console.error("Linguify: Translation error:", error);
    } finally {
      this.setLoading(false);
    }
  }

  private async fetchTranslations(
    texts: string[],
    sourceLang: string,
    targetLang: string
  ): Promise<string[] | null> {
    if (!this.config) return null;

    try {
      const request: TranslationRequest = {
        texts,
        sourceLang,
        targetLang,
        siteApiKey: this.config.apiKey,
      };

      const response = await this.fetchWithRetry(
        `${this.getApiBaseUrl()}/api/widget/translate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: TranslationResponse = await response.json();
      return data.translations;
    } catch (error) {
      console.error("Linguify: Fetch translations error:", error);
      return null;
    }
  }

  private applyTranslations(targetLang: string): void {
    const cache = this.translationCache.get(targetLang);
    if (!cache) return;

    this.textNodes.forEach((textNode) => {
      const translation = cache[textNode.originalText];
      if (translation && textNode.node.textContent !== translation) {
        textNode.node.textContent = translation;
      }
    });
  }

  private restoreOriginalText(): void {
    this.textNodes.forEach((textNode) => {
      if (
        textNode.node.textContent !== textNode.originalText
      ) {
        textNode.node.textContent = textNode.originalText;
      }
    });
  }

  // Check if a node (or any ancestor) matches the configured dynamic
  // selectors. When no selectors are configured, all nodes qualify (opt-out).
  private matchesDynamicSelectors(node: Node): boolean {
    if (!this.dynamicSelectors.length) return true; // no selectors = translate everything
    let el = node.nodeType === Node.ELEMENT_NODE
      ? (node as Element)
      : node.parentElement;
    while (el) {
      for (const sel of this.dynamicSelectors) {
        try {
          if (el.matches(sel)) return true;
        } catch {
          // invalid selector, skip
        }
      }
      el = el.parentElement;
    }
    return false;
  }

  // Check if a node should be ignored (same logic as collectTextNodes filter)
  private shouldIgnoreNode(node: Node): boolean {
    const selectors = this.config?.ignoreSelectors || [];
    let parent = node.parentElement;
    while (parent) {
      if (parent.hasAttribute("data-linguify-ignore")) return true;
      if (parent.hasAttribute("data-notranslate")) return true;
      // Google Translate's own opt-out class. Landing HTML uses it on
      // plan-name divs so a DOM-swap run after DOMContentLoaded can't
      // overwrite 'Free' / 'Starter' / 'Business' / 'Pro' with the
      // cached mistranslations those strings acquired under earlier
      // (buggy) protectTerms regexes.
      if (parent.classList && parent.classList.contains("notranslate")) return true;
      if (parent.id === "linguify-widget-container" || parent.id === "linguify-menu") return true;
      for (const sel of selectors) {
        try {
          if (parent.matches(sel)) return true;
        } catch {
          // invalid selector
        }
      }
      parent = parent.parentElement;
    }
    const parentElement = node.parentElement;
    if (
      !parentElement ||
      /^(script|style|svg|textarea|input|code|pre)$/i.test(parentElement.tagName)
    ) {
      return true;
    }
    const text = node.textContent || "";
    return !text.trim() || text.trim().length < 2;
  }

  // Extract text nodes from a subtree that qualify for translation
  private extractTextNodesFromSubtree(root: Node): Node[] {
    const nodes: Node[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n: Node) => {
        if (this.shouldIgnoreNode(n)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let n: Node | null;
    while ((n = walker.nextNode())) nodes.push(n);
    return nodes;
  }

  // Process batched mutation nodes: register them in textNodes and translate
  private async processDynamicNodes(): Promise<void> {
    if (!this.pendingMutationNodes.size) return;
    const nodes = Array.from(this.pendingMutationNodes);
    this.pendingMutationNodes.clear();

    const sourceLang = this.config?.sourceLanguage || "en";
    if (this.currentLanguage === "original" || this.currentLanguage === sourceLang) return;

    // Register new text nodes that aren't already tracked
    let added = 0;
    const existingNodes = new Set(Array.from(this.textNodes.values()).map((tn) => tn.node));
    let nextIndex = this.textNodes.size;
    for (const node of nodes) {
      if (existingNodes.has(node)) continue;
      if (!node.parentElement || !document.body.contains(node)) continue;
      const raw = node.textContent || "";
      if (raw.trim().length < 2) continue;
      const key = `node_${nextIndex++}`;
      this.textNodes.set(key, { node, originalText: raw, key });
      added++;
    }

    if (added > 0) {
      await this.translateAllText(this.currentLanguage);
    }
  }

  private setupMutationObserver(): void {
    // Full re-collect handler for route changes (SPA navigations).
    const handleFullRecollect = () => {
      if (this.mutationTimeout) {
        clearTimeout(this.mutationTimeout);
      }

      this.mutationTimeout = window.setTimeout(() => {
        this.collectTextNodes();

        const sourceLang = this.config?.sourceLanguage || "en";
        if (
          this.currentLanguage === "original" ||
          this.currentLanguage === sourceLang
        ) {
          return;
        }

        setTimeout(() => {
          this.translateAllText(this.currentLanguage);
        }, 100);
      }, 500);
    };

    // Debounced dynamic node processor (100ms batching)
    let dynamicTimeout: number | null = null;
    const scheduleDynamicProcess = () => {
      if (dynamicTimeout) clearTimeout(dynamicTimeout);
      dynamicTimeout = window.setTimeout(() => {
        dynamicTimeout = null;
        this.processDynamicNodes();
      }, 100);
    };

    this.mutationObserver = new MutationObserver((mutations) => {
      let hasStructuralChange = false;

      for (const mutation of mutations) {
        // Skip mutations inside the widget container itself
        if (this.containerElement?.contains(mutation.target)) continue;

        if (mutation.type === "characterData") {
          // Text content of an existing node changed
          const node = mutation.target;
          if (!this.shouldIgnoreNode(node) && this.matchesDynamicSelectors(node)) {
            // Update the original text for this node and queue for translation
            for (const [, tn] of this.textNodes) {
              if (tn.node === node) {
                tn.originalText = node.textContent || "";
                break;
              }
            }
            this.pendingMutationNodes.add(node);
          }
        } else if (mutation.type === "childList") {
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const added = mutation.addedNodes[i];
            if (added.nodeType === Node.TEXT_NODE) {
              if (!this.shouldIgnoreNode(added) && this.matchesDynamicSelectors(added)) {
                this.pendingMutationNodes.add(added);
              }
            } else if (added.nodeType === Node.ELEMENT_NODE) {
              // Element added — check if it or its subtree matches selectors
              if (this.matchesDynamicSelectors(added)) {
                const textNodes = this.extractTextNodesFromSubtree(added);
                for (const tn of textNodes) {
                  if (this.matchesDynamicSelectors(tn)) {
                    this.pendingMutationNodes.add(tn);
                  }
                }
                if (textNodes.length > 10) hasStructuralChange = true;
              }
            }
          }
          // Clean up removed nodes from tracking
          for (let i = 0; i < mutation.removedNodes.length; i++) {
            const removed = mutation.removedNodes[i];
            this.pendingMutationNodes.delete(removed);
          }
        }
      }

      // Large structural changes (SPA route swap) — full re-collect
      if (hasStructuralChange) {
        this.pendingMutationNodes.clear();
        handleFullRecollect();
      } else if (this.pendingMutationNodes.size > 0) {
        scheduleDynamicProcess();
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // SPA navigations (pushState / replaceState / popstate) often swap the
    // route subtree before MutationObserver has a stable signal. Force a
    // re-collect + translate on URL changes so users don't have to re-pick
    // the language after navigating.
    const onRouteChange = () => {
      setTimeout(() => handleFullRecollect(), 50);
    };
    const wrap = (key: "pushState" | "replaceState") => {
      const orig = history[key];
      history[key] = function (this: History, ...args: unknown[]) {
        const ret = orig.apply(this, args as Parameters<History[typeof key]>);
        window.dispatchEvent(new Event("linguify:routechange"));
        return ret;
      } as History[typeof key];
    };
    wrap("pushState");
    wrap("replaceState");
    window.addEventListener("popstate", onRouteChange);
    window.addEventListener("linguify:routechange", onRouteChange);
  }

  private async restoreOrAutoDetect(): Promise<void> {
    if (!this.config) return;
    const sourceLang = this.config.sourceLanguage || "en";

    // URL path prefix takes precedence on edge-hosted sites: /en/foo means
    // "render English". Matches what the edge renderer actually served.
    if (this.isEdgeHost()) {
      const first = window.location.pathname.split("/").filter(Boolean)[0];
      const known = new Set<string>([sourceLang, ...this.config.targetLanguages]);
      if (first && known.has(first)) {
        this.currentLanguage = first === sourceLang ? "original" : first;
        this.updateSelectorDisplay();
        // On edge-hosted sites the page is already rendered in this
        // language by the backend translate pipeline. The text the
        // walker sees IS the translation. Calling translateAllText
        // here sent Spanish-as-English to the API and rewrote the DOM
        // with whatever came back — noisy, slow, and occasionally
        // blank the page while the round-trip was in flight. Skip it.
        return;
      }
    }

    // ?linguify_lang=es lets the dashboard "preview in language" button
    // (and any deep-link) override saved/browser defaults. Persisted so
    // subsequent navigations stick.
    const url = new URL(window.location.href);
    const deepLink = url.searchParams.get("linguify_lang");
    if (deepLink && (deepLink === "original" || this.config.targetLanguages.includes(deepLink))) {
      await this.selectLanguage(deepLink);
      return;
    }

    const saved = this.loadLanguagePreference();

    // Honor a saved preference even on a fresh page load: if it's a real
    // target language, translate immediately so the user lands on the same
    // language they last picked. "original" or the source means no-op.
    if (saved && saved !== "original" && saved !== sourceLang) {
      if (this.config.targetLanguages.includes(saved)) {
        this.currentLanguage = saved;
        this.updateSelectorDisplay();
        await this.translateAllText(saved);
        return;
      }
    }

    // First visit only (nothing saved yet). Auto-pick a language per the
    // site's detection mode. A user's explicit choice always wins (handled
    // above via `saved`), so this never overrides the visitor.
    if (saved) return;

    // Search engines RENDER JavaScript, so this DOES run for them — the old
    // assumption that "crawlers never trigger it" was wrong and quietly wrecked
    // SEO. On an edge-hosted site selectLanguage() does a hard
    // `location.href` navigation, so a crawler landing on the canonical
    // /servicios (es) whose render locale is en gets redirected to
    // /en/servicios. Google then files the canonical URL as "Page with
    // redirect" and drops it from the index, indexing the language variant
    // instead — de-indexing every source-language page on the site. Bots must
    // see each per-language URL exactly as the edge served it; real users still
    // get auto-detect.
    if (this.isBotVisitor()) return;

    const mode = this.config.languageDetection || "browser";
    if (mode === "off") return;
    let detected: string | null = null;
    if (mode === "browser") {
      detected = this.getBrowserLanguage();
    } else if (mode === "geo") {
      detected = await this.detectLanguageByGeo();
    }
    if (
      detected &&
      detected !== sourceLang &&
      this.config.targetLanguages.includes(detected)
    ) {
      await this.selectLanguage(detected);
    }
  }

  // Ask the edge which language matches the visitor's country. Same-origin
  // call: the customer's domain rides the Google Cloud Load Balancer, which
  // stamps the visitor country natively (no Cloudflare) and the edge maps it
  // to one of the site's languages. Returns null if unavailable.
  private async detectLanguageByGeo(): Promise<string | null> {
    try {
      const res = await fetch("/_linguify/geo", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return typeof data.suggestedLang === "string" ? data.suggestedLang : null;
    } catch {
      return null;
    }
  }

  // True for search-engine crawlers / headless automation. Used to suppress
  // the first-visit auto-redirect: bots execute JS, so an auto-navigation to a
  // language variant makes them record the canonical URL as a redirect and drop
  // it from the index. Mirrors the edge's server-side BOT_PATTERNS (keep in
  // sync) plus navigator.webdriver for headless renderers. UA-anchored so a
  // real device like "CUBOT" (contains "bot") is not misclassified.
  private isBotVisitor(): boolean {
    try {
      if ((navigator as any).webdriver) return true;
      const ua = navigator.userAgent || "";
      return /Googlebot|Bingbot|Slurp|DuckDuckBot|Baiduspider|YandexBot|Sogou|Exabot|facebot|facebookexternalhit|Twitterbot|LinkedInBot|Applebot|PetalBot|SemrushBot|AhrefsBot|MJ12bot|DotBot|GPTBot|ClaudeBot|CCBot|crawl|spider|slurp|\bbot\b/i.test(
        ua,
      );
    } catch {
      return false;
    }
  }

  private getBrowserLanguage(): string | null {
    const lang =
      navigator.language ||
      (navigator as any).userLanguage;
    if (!lang) return null;

    // Get the language code (e.g., 'en' from 'en-US')
    const langCode = lang.split("-")[0].toLowerCase();
    return langCode;
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    const indicator = document.getElementById(
      "linguify-loading"
    );
    if (indicator) {
      indicator.style.display = loading ? "block" : "none";
    }
  }

  private async fetchWithRetry(
    url: string,
    options: RequestInit
  ): Promise<Response> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < this.retryAttempts; attempt++) {
      try {
        const response = await fetch(url, options);
        if (response.ok || response.status === 401 || response.status === 403) {
          return response;
        }

        if (attempt < this.retryAttempts - 1) {
          await this.delay(
            this.retryDelay * Math.pow(2, attempt)
          );
        }
      } catch (error) {
        lastError = error as Error;
        if (attempt < this.retryAttempts - 1) {
          await this.delay(
            this.retryDelay * Math.pow(2, attempt)
          );
        }
      }
    }

    throw (
      lastError ||
      new Error(
        "Failed to fetch after retries"
      )
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getApiBaseUrl(): string {
    const cfg = (window as any).LINGUIFY_CONFIG;
    if (cfg && typeof cfg.apiBaseUrl === "string" && cfg.apiBaseUrl) {
      return cfg.apiBaseUrl.replace(/\/$/, "");
    }
    if (window.location.hostname === "localhost") {
      return "http://localhost:3000";
    }
    return "https://api.linguify.site";
  }

  private saveLanguagePreference(lang: string): void {
    try {
      localStorage.setItem("linguify-language", lang);
    } catch (e) {
      // localStorage might be disabled
      console.warn("Linguify: Could not save language preference");
    }
  }

  private loadLanguagePreference(): string | null {
    try {
      return localStorage.getItem("linguify-language");
    } catch (e) {
      return null;
    }
  }

  destroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.mutationTimeout) {
      clearTimeout(this.mutationTimeout);
    }
    if (this.containerElement) {
      this.containerElement.remove();
    }
  }
}

// Auto-initialize when script loads
let linguifyWidget: LinguifyWidget | null = null;

function initLinguify(): void {
  if (linguifyWidget) return;

  linguifyWidget = new LinguifyWidget();
  linguifyWidget.initialize();

  // Expose global API
  (window as any).Linguify = {
    widget: linguifyWidget,
    setLanguage: (lang: string) => linguifyWidget?.["selectLanguage"](lang),
    destroy: () => {
      if (linguifyWidget) {
        linguifyWidget.destroy();
        linguifyWidget = null;
      }
    },
  };

  // Cross-frame control so the dashboard live preview iframe can drive
  // the widget without poking into private internals from outside.
  window.addEventListener("message", (e) => {
    // Only accept postMessage from same origin or the Linguify dashboard
    if (e.origin !== window.location.origin && e.origin !== 'https://app.linguify.site') {
      return;
    }
    const data = e.data as { type?: string; lang?: string };
    if (data && data.type === "linguify:setLanguage" && typeof data.lang === "string") {
      linguifyWidget?.["selectLanguage"](data.lang);
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initLinguify);
} else {
  initLinguify();
}
